/**
 * Maps between the wire's snake_case evidence contract and the camelCase domain types,
 * the same boundary convention as `verificationService.mappers.ts`.
 *
 * `locator` is passed through UNTOUCHED on purpose: it is an opaque `Dict[str, Any]`
 * (`domain/evidence/models.py:148`), so camel-casing `page_number` inside it would break
 * every source jump silently.
 */
import type { FailureMode } from '../types/drbfm.results';
import {
  AVAILABILITY_REASONS,
  EVIDENCE_AVAILABILITIES,
  FINDING_EVIDENCE_AVAILABILITIES,
} from '../types/evidence';
import type {
  AnchorRef,
  EvidenceRef,
  FindingEvidenceAvailability,
  LegacyConversion,
  LegacyConversionNote,
  Provenance,
  ReviewFinding,
} from '../types/evidence';

/**
 * The wire does not carry the availability contract this build expects (#1195) — a backend
 * without it, or with a value this build has not been taught. Names the FIELD only, never
 * the server's value: the message may end up in a log.
 */
export class EvidenceContractError extends Error {
  readonly field: string;

  constructor(field: string) {
    super(`evidence contract: unexpected value for ${field}`);
    this.name = 'EvidenceContractError';
    this.field = field;
  }
}

const oneOf = <T extends string>(value: unknown, allowed: readonly T[], field: string): T => {
  if (typeof value === 'string' && (allowed as readonly string[]).includes(value)) return value as T;
  // Defaulting here would be exactly the client-side inference the ruling forbids.
  throw new EvidenceContractError(field);
};

const locatorOf = (value: unknown): Record<string, unknown> => (
  value && typeof value === 'object' ? value as Record<string, unknown> : {}
);

const mapProvenance = (raw: any): Provenance => ({
  generatorType: raw?.generator_type,
  generatorVersion: raw?.generator_version ?? '',
  createdBy: raw?.created_by ?? null,
  createdAt: raw?.created_at ?? '',
});

/**
 * The backend's combination rule, re-derived only to CHECK the wire's own summary against
 * its refs — never to replace it. A disagreement is a contract error, not a rendering.
 */
const summarise = (evidence: EvidenceRef[]): FindingEvidenceAvailability => {
  if (evidence.length === 0) return 'no_evidence';
  const available = evidence.filter((ref) => ref.availability === 'available').length;
  if (available === evidence.length) return 'available';
  return available === 0 ? 'unavailable' : 'partially_unavailable';
};

const mapEvidenceRef = (raw: any): EvidenceRef => {
  const availability = availabilityOf(raw);
  const sourceId = raw?.source_id == null ? null : String(raw.source_id);
  const contentHash = raw?.content_hash ?? null;
  // #1198 withholds the source id only when it WAS the hash and the hash is withheld, so a
  // null identity on an available ref, or next to a present hash, is a contract error —
  // never a blank link and never defaulted to ''.
  if (sourceId === null && (availability.availability === 'available' || contentHash !== null)) {
    throw new EvidenceContractError('evidence.source_id');
  }
  return {
    sourceId,
    sourceRevisionId: raw?.source_revision_id ?? null,
    locator: locatorOf(raw?.locator),
    contentHash,
    retrievedAt: raw?.retrieved_at ?? '',
    evidenceKind: raw?.evidence_kind,
    ...availability,
  };
};

/** The reason is null exactly when the ref is available; anything else is a contract error. */
const availabilityOf = (raw: any): Pick<EvidenceRef, 'availability' | 'availabilityReason'> => {
  const availability = oneOf(raw?.availability, EVIDENCE_AVAILABILITIES, 'evidence.availability');
  const reason = raw?.availability_reason;
  if (availability === 'available') {
    if (reason != null) throw new EvidenceContractError('evidence.availability_reason');
    return { availability, availabilityReason: null };
  }
  if (reason == null) throw new EvidenceContractError('evidence.availability_reason');
  return {
    availability,
    availabilityReason: oneOf(reason, AVAILABILITY_REASONS, 'evidence.availability_reason'),
  };
};

const mapAnchorRef = (raw: any): AnchorRef => ({
  targetType: raw?.target_type,
  targetId: String(raw?.target_id ?? ''),
  locator: locatorOf(raw?.locator),
  maturity: raw?.maturity,
  createdBy: raw?.created_by ?? null,
  verifiedBy: raw?.verified_by ?? null,
});

/** `claim` and `contentHash` stay null when the server withheld them — never defaulted. */
export const mapReviewFinding = (raw: any): ReviewFinding => {
  const evidence: EvidenceRef[] = (raw?.evidence ?? []).map(mapEvidenceRef);
  const evidenceAvailability = oneOf(
    raw?.evidence_availability, FINDING_EVIDENCE_AVAILABILITIES, 'finding.evidence_availability',
  );
  if (evidenceAvailability !== summarise(evidence)) {
    throw new EvidenceContractError('finding.evidence_availability');
  }
  return mapFindingFields(raw, evidence, evidenceAvailability);
};

const mapFindingFields = (
  raw: any, evidence: EvidenceRef[], evidenceAvailability: FindingEvidenceAvailability,
): ReviewFinding => ({
  id: raw?.id ?? '',
  tenantId: raw?.tenant_id ?? '',
  subjectRevisionId: raw?.subject_revision_id ?? '',
  claim: raw?.claim ?? null,
  severity: raw?.severity,
  confidence: raw?.confidence ?? 0,
  verificationStatus: raw?.verification_status,
  provenance: mapProvenance(raw?.provenance),
  evidence,
  anchors: (raw?.anchors ?? []).map(mapAnchorRef),
  evidenceAvailability,
});

const mapNote = (raw: any): LegacyConversionNote => ({
  reasonCode: raw?.reason_code ?? '',
  detail: raw?.detail ?? '',
  legacyLocator: locatorOf(raw?.legacy_locator),
});

export const mapLegacyConversion = (raw: any): LegacyConversion => ({
  finding: mapReviewFinding(raw?.finding),
  notes: (raw?.notes ?? []).map(mapNote),
});

/**
 * The request payload for `POST /api/v1/evidence/legacy-conversions` with
 * `source_type=drbfm`.
 *
 * Only the keys `application/evidence/legacy_adapters.py::adapt_failure_mode` actually
 * reads are sent, so reviewer notes and scores never leave the client.
 *
 * This is NOT free: `_content_hash` (`legacy_adapters.py:467,519`) digests the whole
 * submitted sub-dict, so the resulting `EvidenceRef.content_hash` depends on what the
 * caller sent. Accepted deliberately — the hash is an integrity/dedup value that this UI
 * neither renders nor copies, and the finding id is unaffected because it keys on
 * `{tenant, subject_revision, generator_type, source_key}` only
 * (`application/evidence/identity.py:116-119`).
 */
export const failureModeToLegacyPayload = (
  failureMode: FailureMode,
): Record<string, unknown> => ({
  id: failureMode.id,
  failure_description: failureMode.failureDescription,
  severity: failureMode.severity,
  cad_anchor: failureMode.cadAnchor && {
    cad_model_id: failureMode.cadAnchor.cadModelId,
    part_label: failureMode.cadAnchor.partLabel,
    position: failureMode.cadAnchor.position,
    source: failureMode.cadAnchor.source,
  },
  drawing_evidence: failureMode.drawingEvidence && {
    document_id: failureMode.drawingEvidence.documentId,
    image_id: failureMode.drawingEvidence.imageId,
    page_number: failureMode.drawingEvidence.pageNumber,
    bbox: failureMode.drawingEvidence.bbox,
    source: failureMode.drawingEvidence.source,
  },
  knowledge_references: (failureMode.knowledgeReferences ?? []).map((reference) => ({
    document_id: reference.documentId,
    category: reference.category,
    page_number: reference.pageNumber,
    image_id: reference.imageId,
  })),
});
